import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card, PageHeader, Spinner, EmptyState, Badge, formatDate, formatDateTime, formatCents } from '../../components/ui';

interface Dashboard {
  upcomingBookings: number;
  outstandingCents: number;
  unreadMessages: number;
}

interface Booking {
  id: string;
  serviceName?: string;
  startsAt: string;
  status: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  totalCents: number;
  status: string;
  dueDate?: string;
}

interface Conversation {
  id: string;
  subject?: string;
  lastMessageAt: string;
  unread: boolean;
}

export function PortalHome() {
  const q = useQuery({
    queryKey: ['portal-dashboard'],
    queryFn: () => api.get<Dashboard>('/portal/dashboard'),
  });
  if (q.isLoading) return <Spinner />;
  const d = q.data!;
  return (
    <div>
      <PageHeader title="Welcome back" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-3)' }}>
        <Stat label="Upcoming bookings" value={d.upcomingBookings} />
        <Stat label="Outstanding" value={formatCents(d.outstandingCents)} />
        <Stat label="Unread messages" value={d.unreadMessages} />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{label}</div>
      <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 700 }}>{value}</div>
    </Card>
  );
}

export function PortalBookings() {
  const q = useQuery({
    queryKey: ['portal-bookings'],
    queryFn: () => api.get<{ data: Booking[] }>('/portal/bookings').catch(() => ({ data: [] })),
  });
  if (q.isLoading) return <Spinner />;
  const items = q.data?.data ?? [];
  return (
    <div>
      <PageHeader title="My bookings" />
      {items.length === 0 ? (
        <Card><EmptyState title="No bookings yet" /></Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {items.map((b) => (
            <Card key={b.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{b.serviceName ?? 'Booking'}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{formatDateTime(b.startsAt)}</div>
                </div>
                <Badge color={b.status === 'confirmed' ? 'green' : 'blue'}>{b.status}</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function PortalInvoices() {
  const q = useQuery({
    queryKey: ['portal-invoices'],
    queryFn: () => api.get<{ data: Invoice[] }>('/portal/invoices').catch(() => ({ data: [] })),
  });
  if (q.isLoading) return <Spinner />;
  const items = q.data?.data ?? [];
  return (
    <div>
      <PageHeader title="My invoices" />
      {items.length === 0 ? (
        <Card><EmptyState title="No invoices" /></Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {items.map((i) => (
            <Card key={i.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>#{i.invoiceNumber}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                    Due {i.dueDate ? formatDate(i.dueDate) : '—'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700 }}>{formatCents(i.totalCents)}</div>
                  <Badge color={i.status === 'paid' ? 'green' : 'yellow'}>{i.status}</Badge>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function PortalMessages() {
  const q = useQuery({
    queryKey: ['portal-messages'],
    queryFn: () => api.get<{ data: Conversation[] }>('/portal/conversations').catch(() => ({ data: [] })),
  });
  if (q.isLoading) return <Spinner />;
  const items = q.data?.data ?? [];
  return (
    <div>
      <PageHeader title="Messages" />
      {items.length === 0 ? (
        <Card><EmptyState title="No messages" /></Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {items.map((c) => (
            <Card key={c.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: c.unread ? 700 : 500 }}>{c.subject ?? 'Conversation'}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{formatDateTime(c.lastMessageAt)}</div>
                </div>
                {c.unread && <Badge color="blue">new</Badge>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function PortalProfile() {
  const q = useQuery({
    queryKey: ['portal-profile'],
    queryFn: () => api.get<{ name: string; email: string; phone?: string }>('/portal/profile'),
  });
  if (q.isLoading) return <Spinner />;
  const p = q.data!;
  return (
    <div>
      <PageHeader title="Profile" />
      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>Name</div>
            <div style={{ fontSize: 'var(--text-base)' }}>{p.name}</div>
          </div>
          <div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>Email</div>
            <div style={{ fontSize: 'var(--text-base)' }}>{p.email}</div>
          </div>
          {p.phone && (
            <div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>Phone</div>
              <div style={{ fontSize: 'var(--text-base)' }}>{p.phone}</div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
